import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Calendar } from '../ui/calendar';
import { Alert, AlertDescription } from '../ui/alert';
import {
  Plus, Brain, Users, CheckCircle, Clock, Loader2, Calendar as CalendarIcon,
  ChevronRight, Trash2, Video, X, PlayCircle, CheckCircle2, AlertTriangle,
  ExternalLink, GraduationCap, Edit, ArrowLeft
} from 'lucide-react';
import { performanceApi, employeeApi, leaveApi } from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import { format, isSameDay, isWithinInterval, parseISO } from 'date-fns';
import { cn } from '../../lib/utils';

interface KTSession {
  id: string;
  knowledge_transfer_id: string;
  session_number: number;
  title: string;
  description?: string;
  scheduled_date: string;
  duration_minutes: number;
  status: 'SCHEDULED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
  meeting_link?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

interface KnowledgeTransfer {
  id: string;
  title: string;
  description?: string;
  mentor_id?: string;
  mentee_id?: string;
  employee_id?: string;
  reviewer_id?: string;
  target_date?: string;
  status: string;
  progress?: number;
  sessions_completed?: number;
  sessions_total?: number;
  goal_type?: string;
  type?: string;
}

const KnowledgeTransfers: React.FC = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [selectedKT, setSelectedKT] = useState<KnowledgeTransfer | null>(null);
  const [isSessionDialogOpen, setIsSessionDialogOpen] = useState(false);
  const [editingSession, setEditingSession] = useState<KTSession | null>(null);

  const [statusFilter, setStatusFilter] = useState<string>('all');

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    mentor_id: '',
    mentee_id: '',
    target_date: new Date().toISOString().split('T')[0],
    status: 'IN_PROGRESS',
  });

  const [sessionFormData, setSessionFormData] = useState({
    title: '',
    description: '',
    scheduled_date: new Date(),
    duration_minutes: 60,
    meeting_link: '',
    notes: '',
  });

  const { data: knowledgeTransfers = [], isLoading } = useQuery({
    queryKey: ['knowledgeTransfers'],
    queryFn: () => performanceApi.feedback.getAll(),
  });

  const { data: employees = [] } = useQuery({
    queryKey: ['employees'],
    queryFn: () => employeeApi.getAll({}),
  });

  // Fetch all leave requests to check for conflicts
  const { data: leaveRequests = [] } = useQuery({
    queryKey: ['leaveRequests'],
    queryFn: () => leaveApi.requests.getAll(),
  });

  // Fetch sessions for selected KT
  const { data: ktProgress, isLoading: isLoadingProgress } = useQuery({
    queryKey: ['ktProgress', selectedKT?.id],
    queryFn: () => performanceApi.ktSessions.getProgress(selectedKT!.id),
    enabled: !!selectedKT?.id,
  });

  // Check if an employee is on leave on a specific date
  const checkLeaveConflict = useMemo(() => {
    return (employeeId: string, date: Date): { isOnLeave: boolean; leaveName?: string } => {
      if (!employeeId || !date) return { isOnLeave: false };

      const approvedLeaves = leaveRequests.filter((leave: any) =>
        leave.employee_id === employeeId &&
        (leave.status === 'APPROVED' || leave.status === 'PENDING')
      );

      for (const leave of approvedLeaves) {
        const startDate = parseISO(leave.start_date);
        const endDate = parseISO(leave.end_date);

        if (isWithinInterval(date, { start: startDate, end: endDate }) ||
          isSameDay(date, startDate) ||
          isSameDay(date, endDate)) {
          const employee = employees.find((e: any) => e.id === employeeId);
          const employeeName = employee
            ? `${employee.first_name} ${employee.last_name || ''}`.trim()
            : 'Employee';
          return {
            isOnLeave: true,
            leaveName: `${employeeName} is on ${leave.status === 'PENDING' ? 'pending ' : ''}leave (${leave.start_date} to ${leave.end_date})`
          };
        }
      }
      return { isOnLeave: false };
    };
  }, [leaveRequests, employees]);

  // Get leave conflicts for the selected session date
  const sessionLeaveConflicts = useMemo(() => {
    if (!selectedKT || !sessionFormData.scheduled_date) return [];

    const conflicts: string[] = [];
    const mentorId = selectedKT.reviewer_id || selectedKT.mentor_id;
    const menteeId = selectedKT.employee_id || selectedKT.mentee_id;

    if (mentorId) {
      const mentorConflict = checkLeaveConflict(mentorId, sessionFormData.scheduled_date);
      if (mentorConflict.isOnLeave) {
        conflicts.push(`Mentor: ${mentorConflict.leaveName}`);
      }
    }

    if (menteeId) {
      const menteeConflict = checkLeaveConflict(menteeId, sessionFormData.scheduled_date);
      if (menteeConflict.isOnLeave) {
        conflicts.push(`Mentee: ${menteeConflict.leaveName}`);
      }
    }

    return conflicts;
  }, [selectedKT, sessionFormData.scheduled_date, checkLeaveConflict]);

  const createMutation = useMutation({
    mutationFn: performanceApi.feedback.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledgeTransfers'] });
      setIsCreateOpen(false);
      setFormData({ title: '', description: '', mentor_id: '', mentee_id: '', target_date: new Date().toISOString().split('T')[0], status: 'IN_PROGRESS' });
      toast({ title: 'Knowledge transfer created successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const updateTransferMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => performanceApi.feedback.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledgeTransfers'] });
      toast({ title: 'Transfer status updated successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const createSessionMutation = useMutation({
    mutationFn: performanceApi.ktSessions.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ktProgress', selectedKT?.id] });
      queryClient.invalidateQueries({ queryKey: ['knowledgeTransfers'] });
      setIsSessionDialogOpen(false);
      resetSessionForm();
      toast({ title: 'Session created successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const updateSessionMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => performanceApi.ktSessions.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ktProgress', selectedKT?.id] });
      queryClient.invalidateQueries({ queryKey: ['knowledgeTransfers'] });
      setIsSessionDialogOpen(false);
      setEditingSession(null);
      resetSessionForm();
      toast({ title: 'Session updated successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const deleteSessionMutation = useMutation({
    mutationFn: performanceApi.ktSessions.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ktProgress', selectedKT?.id] });
      queryClient.invalidateQueries({ queryKey: ['knowledgeTransfers'] });
      toast({ title: 'Session deleted successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const resetSessionForm = () => {
    setSessionFormData({
      title: '',
      description: '',
      scheduled_date: new Date(),
      duration_minutes: 60,
      meeting_link: '',
      notes: '',
    });
  };

  const getEmployeeName = (employeeId: string) => {
    const employee = employees.find((e: any) => e.id === employeeId);
    return employee ? `${employee.first_name} ${employee.last_name || ''}` : 'Unknown';
  };

  const getEmployeeInitials = (name: string) => {
    const parts = name.trim().split(' ');
    const first = parts[0]?.charAt(0) || '';
    const last = parts[parts.length - 1]?.charAt(0) || '';
    return (first + last).toUpperCase() || 'U';
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return 'N/A';
    return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(new Date(dateStr));
  };

  const formatDateTime = (dateStr: string) => {
    if (!dateStr) return 'N/A';
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    }).format(new Date(dateStr));
  };

  const getStatusBadge = (status: string) => {
    const statusColors: Record<string, string> = {
      COMPLETED: 'bg-emerald-50 text-emerald-700 border-emerald-200/50 dark:bg-emerald-900/20 dark:text-emerald-400',
      IN_PROGRESS: 'bg-blue-50 text-blue-700 border-blue-200/50 dark:bg-blue-900/20 dark:text-blue-400',
      ON_TRACK: 'bg-emerald-50 text-emerald-700 border-emerald-200/50 dark:bg-emerald-900/20 dark:text-emerald-400',
      PENDING: 'bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300',
      SCHEDULED: 'bg-amber-50 text-amber-700 border-amber-200/50 dark:bg-amber-900/20 dark:text-amber-400',
      AT_RISK: 'bg-rose-50 text-rose-700 border-rose-200/50 dark:bg-rose-900/20 dark:text-rose-400',
      BEHIND: 'bg-rose-50 text-rose-700 border-rose-200/50 dark:bg-rose-900/20 dark:text-rose-400',
      CANCELLED: 'bg-slate-100 text-slate-500 border-slate-200 dark:bg-slate-800 dark:text-slate-400',
    };
    const key = status?.toUpperCase() || 'IN_PROGRESS';
    const classNames = statusColors[key] || 'bg-slate-50 text-slate-700 border-slate-200';
    const label = status.replace('_', ' ');
    return (
      <Badge className={cn("px-2.5 py-0.5 rounded-lg font-bold text-[10px] tracking-wide shadow-none border shrink-0", classNames)} variant="outline">
        {label}
      </Badge>
    );
  };

  const getSessionStatusIcon = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'COMPLETED': 
        return (
          <div className="h-7 w-7 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-900/30 flex items-center justify-center text-emerald-600 shrink-0">
            <CheckCircle2 className="h-4 w-4" />
          </div>
        );
      case 'IN_PROGRESS': 
        return (
          <div className="h-7 w-7 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-900/30 flex items-center justify-center text-blue-600 shrink-0">
            <PlayCircle className="h-4 w-4 animate-pulse" />
          </div>
        );
      case 'SCHEDULED': 
        return (
          <div className="h-7 w-7 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-900/30 flex items-center justify-center text-amber-500 shrink-0">
            <Clock className="h-4 w-4" />
          </div>
        );
      case 'CANCELLED': 
        return (
          <div className="h-7 w-7 rounded-lg bg-slate-50 dark:bg-slate-800/80 border border-slate-150 flex items-center justify-center text-slate-400 shrink-0">
            <X className="h-4 w-4" />
          </div>
        );
      default: 
        return (
          <div className="h-7 w-7 rounded-lg bg-slate-50 border border-slate-150 flex items-center justify-center text-slate-400 shrink-0">
            <Clock className="h-4 w-4" />
          </div>
        );
    }
  };

  const handleCreate = () => {
    if (!formData.title || !formData.mentor_id || !formData.mentee_id) {
      toast({ title: 'Error', description: 'Please fill all required fields', variant: 'destructive' });
      return;
    }
    if (formData.mentor_id === formData.mentee_id) {
      toast({ title: 'Error', description: 'Mentor and mentee cannot be the same person', variant: 'destructive' });
      return;
    }
    createMutation.mutate({
      title: formData.title,
      description: formData.description,
      employee_id: formData.mentee_id,
      reviewer_id: formData.mentor_id,
      mentor_id: formData.mentor_id,
      mentee_id: formData.mentee_id,
      target_date: formData.target_date,
      status: formData.status,
      goal_type: 'KNOWLEDGE_TRANSFER',
      feedback_type: 'KNOWLEDGE_TRANSFER',
    });
  };

  const handleOpenSessionDialog = (session?: KTSession) => {
    if (session) {
      // Edit mode - populate form with session data
      setEditingSession(session);
      setSessionFormData({
        title: session.title,
        description: session.description || '',
        scheduled_date: new Date(session.scheduled_date),
        duration_minutes: session.duration_minutes,
        meeting_link: session.meeting_link || '',
        notes: session.notes || '',
      });
    } else {
      // Create mode - reset form
      setEditingSession(null);
      resetSessionForm();
    }
    setIsSessionDialogOpen(true);
  };

  const handleSaveSession = () => {
    if (!sessionFormData.title) {
      toast({ title: 'Error', description: 'Session title is required', variant: 'destructive' });
      return;
    }

    // Check for leave conflicts
    if (sessionLeaveConflicts.length > 0) {
      toast({
        title: 'Cannot Schedule Session',
        description: 'One or more participants are on leave on this date. Please choose a different date.',
        variant: 'destructive'
      });
      return;
    }

    if (editingSession) {
      // Update existing session
      const sessionData = {
        title: sessionFormData.title,
        description: sessionFormData.description || undefined,
        scheduled_date: sessionFormData.scheduled_date.toISOString(),
        duration_minutes: sessionFormData.duration_minutes,
        meeting_link: sessionFormData.meeting_link || undefined,
        notes: sessionFormData.notes || undefined,
      };

      updateSessionMutation.mutate({
        id: editingSession.id,
        data: sessionData
      });
    } else {
      // Create new session
      const sessions = ktProgress?.sessions || [];
      const nextSessionNumber = sessions.length + 1;

      const sessionData = {
        knowledge_transfer_id: selectedKT!.id,
        session_number: nextSessionNumber,
        title: sessionFormData.title,
        description: sessionFormData.description || undefined,
        scheduled_date: sessionFormData.scheduled_date.toISOString(),
        duration_minutes: sessionFormData.duration_minutes,
        meeting_link: sessionFormData.meeting_link || undefined,
        notes: sessionFormData.notes || undefined,
        status: 'SCHEDULED',
      };

      createSessionMutation.mutate(sessionData);
    }
  };

  const handleUpdateSessionStatus = (session: KTSession, newStatus: string) => {
    updateSessionMutation.mutate({
      id: session.id,
      data: { status: newStatus }
    });
  };

  const handleDeleteSession = (sessionId: string) => {
    if (confirm('Are you sure you want to delete this session?')) {
      deleteSessionMutation.mutate(sessionId);
    }
  };

  const ktGoals = knowledgeTransfers.filter((g: any) => g.goal_type === 'KNOWLEDGE_TRANSFER' || g.type === 'KNOWLEDGE_TRANSFER');
  const completedKT = ktGoals.filter((g: any) => g.status === 'COMPLETED').length;
  const activeKT = ktGoals.filter((g: any) => g.status === 'IN_PROGRESS' || g.status === 'ON_TRACK').length;
  const atRiskKT = ktGoals.filter((g: any) => g.status === 'AT_RISK' || g.status === 'BEHIND').length;

  // Apply status filter
  const filteredKTGoals = statusFilter === 'all'
    ? ktGoals
    : statusFilter === 'active'
      ? ktGoals.filter((g: any) => g.status === 'IN_PROGRESS' || g.status === 'ON_TRACK')
      : statusFilter === 'completed'
        ? ktGoals.filter((g: any) => g.status === 'COMPLETED')
        : statusFilter === 'at_risk'
          ? ktGoals.filter((g: any) => g.status === 'AT_RISK' || g.status === 'BEHIND')
          : ktGoals;

  const getFilterLabel = () => {
    switch (statusFilter) {
      case 'active': return 'Active';
      case 'completed': return 'Completed';
      case 'at_risk': return 'At Risk';
      default: return 'All';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="kt-loading">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-2 font-bold text-slate-400">Loading knowledge transfers...</span>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto space-y-8 p-6 custom-scrollbar animate-in fade-in duration-300" data-testid="knowledge-transfers-page">
      {/* Header section with inline controls */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold text-[#143A62] dark:text-slate-100 tracking-tight">Manage skill transfers and mentorship programs</h2>
          
        </div>
        
        <div className="flex flex-wrap items-center gap-3 self-start md:self-auto">
          <Button 
            onClick={() => setIsCreateOpen(true)} 
            data-testid="new-transfer-btn"
          >
            <Plus className="h-4 w-4 mr-1.5" />
            New Transfer
          </Button>
        </div>
      </div>

      {/* Stats Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="border border-slate-100 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900 overflow-hidden hover:translate-y-[-2px] transition-all duration-300">
          <CardContent className="p-6">
            <div className="flex justify-between items-start">
              <div className="space-y-4">
                <p className="text-sm font-bold text-slate-400 tracking-wide">Total Transfers</p>
                <div className="space-y-1">
                  <h3 className="text-4xl font-bold text-slate-800 dark:text-white leading-none" data-testid="total-transfers-count">{ktGoals.length}</h3>
                  <p className="text-[12px] font-bold text-slate-400 tracking-wide mt-1">Plans Set</p>
                </div>
              </div>
              <div className="p-3 rounded-2xl bg-blue-50 dark:bg-blue-900/20 text-[#143A62] dark:text-blue-400 shadow-sm">
                <Brain className="h-6 w-6" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-slate-100 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900 overflow-hidden hover:translate-y-[-2px] transition-all duration-300">
          <CardContent className="p-6">
            <div className="flex justify-between items-start">
              <div className="space-y-4">
                <p className="text-sm font-bold text-slate-400 tracking-wide">Active</p>
                <div className="space-y-1">
                  <h3 className="text-4xl font-bold text-slate-800 dark:text-white leading-none" data-testid="active-transfers-count">{activeKT}</h3>
                  <p className="text-[12px] font-bold text-slate-400 tracking-wide mt-1">In Progress</p>
                </div>
              </div>
              <div className="p-3 rounded-2xl bg-amber-50 dark:bg-amber-900/20 text-amber-500 shadow-sm">
                <Clock className="h-6 w-6" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-slate-100 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900 overflow-hidden hover:translate-y-[-2px] transition-all duration-300">
          <CardContent className="p-6">
            <div className="flex justify-between items-start">
              <div className="space-y-4">
                <p className="text-sm font-bold text-slate-400 tracking-wide">Completed</p>
                <div className="space-y-1">
                  <h3 className="text-4xl font-bold text-slate-800 dark:text-white leading-none" data-testid="completed-transfers-count">{completedKT}</h3>
                  <p className="text-[12px] font-bold text-slate-400 tracking-wide mt-1">Graduated</p>
                </div>
              </div>
              <div className="p-3 rounded-2xl bg-emerald-50 dark:bg-emerald-900/20 text-emerald-500 shadow-sm">
                <CheckCircle className="h-6 w-6" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-slate-100 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900 overflow-hidden hover:translate-y-[-2px] transition-all duration-300">
          <CardContent className="p-6">
            <div className="flex justify-between items-start">
              <div className="space-y-4">
                <p className="text-sm font-bold text-slate-400 tracking-wide">At Risk</p>
                <div className="space-y-1">
                  <h3 className="text-4xl font-bold text-slate-800 dark:text-white leading-none" data-testid="at-risk-transfers-count">{atRiskKT}</h3>
                  <p className="text-[12px] font-bold text-slate-400 tracking-wide mt-1">Need Attention</p>
                </div>
              </div>
              <div className="p-3 rounded-2xl bg-rose-50 dark:bg-rose-900/20 text-rose-500 shadow-sm">
                <Users className="h-6 w-6" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content - Two Column Layout when KT is selected */}
      <div className={cn("grid gap-6 items-start", selectedKT ? "grid-cols-1 lg:grid-cols-2" : "grid-cols-1")}>
        {/* Knowledge Transfers List */}
        <Card className={cn("border border-slate-100 dark:border-slate-800 shadow-md bg-white dark:bg-slate-900 overflow-hidden rounded-2xl", selectedKT && "hidden lg:block")}>
          <CardHeader className="pb-5 pt-6 px-8 border-b border-slate-50 dark:border-slate-800/50 flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-xl font-bold text-[#143A62] dark:text-slate-100 tracking-tight">{getFilterLabel()} Knowledge Transfers</CardTitle>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="border-slate-200 dark:border-slate-850 bg-white dark:bg-slate-900 focus:ring-0 focus:ring-offset-0 font-bold text-xs h-9 w-[150px] rounded-xl shadow-sm">
                <SelectValue placeholder="Filter status" />
              </SelectTrigger>
              <SelectContent className="border-slate-100 dark:border-slate-800">
                <SelectItem value="all" className="text-xs font-bold">All ({ktGoals.length})</SelectItem>
                <SelectItem value="active" className="text-xs font-bold">Active ({activeKT})</SelectItem>
                <SelectItem value="completed" className="text-xs font-bold">Completed ({completedKT})</SelectItem>
                <SelectItem value="at_risk" className="text-xs font-bold">At Risk ({atRiskKT})</SelectItem>
              </SelectContent>
            </Select>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent border-b-2 border-slate-50 dark:border-slate-800">
                  <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12 px-8">Transfer Title</TableHead>
                  <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Mentee</TableHead>
                  <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Progress</TableHead>
                  <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Target Date</TableHead>
                  <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Status</TableHead>
                  <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12 text-right px-8"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredKTGoals.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-20 px-8">
                      <div className="flex flex-col items-center">
                        <div className="bg-slate-50 dark:bg-slate-800 h-14 w-14 rounded-2xl flex items-center justify-center mb-4 border border-slate-100 dark:border-slate-700">
                          <Brain className="h-7 w-7 text-slate-350 dark:text-slate-650" />
                        </div>
                        <h3 className="text-base font-bold text-slate-800 dark:text-slate-200">No knowledge transfers found</h3>
                        <p className="text-xs font-semibold text-slate-400 mt-1 max-w-sm">
                          {statusFilter === 'all'
                            ? 'Create your first knowledge transfer path to get started.'
                            : `No ${getFilterLabel().toLowerCase()} knowledge transfers. Try another status filter.`
                          }
                        </p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredKTGoals.map((transfer: KnowledgeTransfer) => {
                    const menteeName = getEmployeeName(transfer.employee_id || transfer.mentee_id || '');
                    const menteeInitials = getEmployeeInitials(menteeName);
                    return (
                      <TableRow
                        key={transfer.id}
                        className={cn(
                          "cursor-pointer hover:bg-slate-50/50 dark:hover:bg-slate-800/50 transition-colors border-b border-slate-50 dark:border-slate-850/50",
                          selectedKT?.id === transfer.id && "bg-slate-50/80 dark:bg-slate-800/60"
                        )}
                        onClick={() => setSelectedKT(transfer)}
                        data-testid={`kt-row-${transfer.id}`}
                      >
                        <TableCell className="px-8 py-4">
                          <div>
                            <div className="font-bold text-slate-800 dark:text-slate-100 text-sm tracking-tight">{transfer.title}</div>
                            {transfer.description && (
                              <div className="text-[11px] font-bold text-slate-400 truncate max-w-[200px] mt-0.5">
                                {transfer.description}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="py-4">
                          <div className="flex items-center gap-2">
                            <div className="h-6 w-6 rounded-md bg-gradient-to-br from-[#143A62]/10 to-blue-500/10 text-[#143A62] dark:text-blue-400 font-bold text-[9px] flex items-center justify-center border border-slate-100/50 shrink-0">
                              {menteeInitials}
                            </div>
                            <span className="font-bold text-xs text-slate-700 dark:text-slate-300">{menteeName}</span>
                          </div>
                        </TableCell>
                        <TableCell className="py-4">
                          <div className="flex items-center space-x-2">
                            <Progress
                              value={transfer.progress || (transfer.status === 'COMPLETED' ? 100 : 0)}
                              className="h-2 w-16"
                            />
                            <span className="text-[11px] font-bold text-slate-650 dark:text-slate-350 shrink-0">
                              {transfer.sessions_total
                                ? `${transfer.sessions_completed || 0}/${transfer.sessions_total}`
                                : `${transfer.progress || 0}%`
                              }
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-xs font-bold text-slate-650 dark:text-slate-350 py-4">
                          {formatDate(transfer.target_date || '')}
                        </TableCell>
                        <TableCell className="py-4" onClick={(e) => e.stopPropagation()}>
                          <Select
                            value={transfer.status}
                            onValueChange={(value) => {
                              updateTransferMutation.mutate({ id: transfer.id, data: { status: value } });
                            }}
                          >
                            <SelectTrigger className="h-8 w-[120px] text-xs font-bold rounded-xl border-slate-200 dark:border-slate-850 bg-white dark:bg-slate-900 shadow-sm focus:ring-0">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="border-slate-100 dark:border-slate-800">
                              <SelectItem value="IN_PROGRESS" className="text-xs font-bold">In Progress</SelectItem>
                              <SelectItem value="ON_TRACK" className="text-xs font-bold">On Track</SelectItem>
                              <SelectItem value="COMPLETED" className="text-xs font-bold">Completed</SelectItem>
                              <SelectItem value="AT_RISK" className="text-xs font-bold">At Risk</SelectItem>
                              <SelectItem value="BEHIND" className="text-xs font-bold">Behind</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="px-8 py-4 text-right">
                          <ChevronRight className={cn(
                            "h-4 w-4 text-slate-400 transition-transform ml-auto",
                            selectedKT?.id === transfer.id && "rotate-90 text-[#143A62]"
                          )} />
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Sessions Panel */}
        {selectedKT && (
          <Card className="border border-slate-100 dark:border-slate-800 shadow-md bg-white dark:bg-slate-900 overflow-hidden rounded-2xl animate-in slide-in-from-right duration-350" data-testid="kt-sessions-panel">
            <CardHeader className="pb-5 pt-6 px-8 border-b border-slate-50 dark:border-slate-800/50 flex flex-row items-center justify-between space-y-0 gap-4">
              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setSelectedKT(null)}
                  title="Go back to list"
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <div>
                  <CardTitle className="text-xl font-bold text-[#143A62] dark:text-slate-100 tracking-tight">{selectedKT.title}</CardTitle>
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs font-bold text-slate-400 mt-1">
                    <span>Mentor: {getEmployeeName(selectedKT.reviewer_id || selectedKT.mentor_id || '')}</span>
                    <ChevronRight className="h-3 w-3 text-slate-355" />
                    <span>Mentee: {getEmployeeName(selectedKT.employee_id || selectedKT.mentee_id || '')}</span>
                  </div>
                </div>
              </div>
              <Button
                onClick={() => handleOpenSessionDialog()}
                data-testid="add-session-btn"
              >
                <Plus className="h-3.5 w-3.5 mr-1" />
                Add Session
              </Button>
            </CardHeader>
            <CardContent className="p-8 space-y-6">
              {/* Progress Bar Banner */}
              <div className="bg-slate-50/50 dark:bg-slate-900/50 border border-slate-100 dark:border-slate-800/80 p-4 rounded-2xl">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold text-slate-700 dark:text-slate-300">Overall Plan Progress</span>
                  <span className="text-xs font-black bg-blue-50 dark:bg-blue-900/30 px-2 py-0.5 rounded-md text-[#143A62] dark:text-blue-400" data-testid="kt-progress-value">
                    {ktProgress?.progress || 0}%
                  </span>
                </div>
                <Progress value={ktProgress?.progress || 0} className="h-2 shadow-sm" />
                <p className="text-[10px] font-bold text-slate-400 mt-2">
                  {ktProgress?.sessions_completed || 0} of {ktProgress?.sessions_total || 0} sessions completed
                </p>
              </div>

              {/* Sessions List */}
              {isLoadingProgress ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                </div>
              ) : (
                <div className="space-y-4" data-testid="sessions-list">
                  {(!ktProgress?.sessions || ktProgress.sessions.length === 0) ? (
                    <div className="text-center py-10 border border-dashed rounded-2xl bg-slate-50/30 dark:bg-slate-900/10">
                      <Video className="h-8 w-8 mx-auto mb-2 text-slate-300 dark:text-slate-700" />
                      <p className="text-xs font-bold text-slate-400">No sessions scheduled yet</p>
                      <Button 
                        size="sm" 
                        variant="link" 
                        onClick={() => handleOpenSessionDialog()}
                      >
                        Schedule the first session
                      </Button>
                    </div>
                  ) : (
                    ktProgress.sessions.map((session: KTSession) => (
                      <div
                        key={session.id}
                        className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800/80 rounded-xl p-4 space-y-3 hover:translate-y-[-1px] transition-all duration-300 shadow-sm"
                        data-testid={`session-${session.id}`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-start space-x-3">
                            {getSessionStatusIcon(session.status)}
                            <div className="space-y-1">
                              <p className="font-bold text-sm text-slate-800 dark:text-slate-200">
                                Session {session.session_number}: {session.title}
                              </p>
                              <p className="text-[11px] font-bold text-slate-400 flex flex-wrap gap-x-2 gap-y-0.5 items-center">
                                <span className="text-slate-650 dark:text-slate-350">{formatDateTime(session.scheduled_date)}</span>
                                <span className="text-slate-200 dark:text-slate-800">•</span>
                                <span>{session.duration_minutes} min</span>
                              </p>
                              {session.description && (
                                <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 mt-1 line-clamp-2 bg-slate-50/50 dark:bg-slate-850/50 p-2 rounded-lg border border-slate-100/50">
                                  {session.description}
                                </p>
                              )}
                              {session.meeting_link && (
                                <a
                                  href={session.meeting_link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs font-bold text-blue-600 hover:underline mt-1.5 inline-flex items-center gap-1.5"
                                >
                                  <Video className="h-3.5 w-3.5" />
                                  Join Session Meeting
                                </a>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex items-center space-x-1 shrink-0">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleOpenSessionDialog(session)}
                              data-testid={`edit-session-${session.id}`}
                            >
                              <Edit className="h-3.5 w-3.5" />
                            </Button>
                            
                            <Select
                              value={session.status}
                              onValueChange={(value) => handleUpdateSessionStatus(session, value)}
                            >
                              <SelectTrigger className="h-8 w-[110px] text-xs font-bold rounded-xl border-slate-200 bg-white shadow-sm focus:ring-0">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="border-slate-100 dark:border-slate-800">
                                <SelectItem value="SCHEDULED" className="text-xs font-bold">Scheduled</SelectItem>
                                <SelectItem value="IN_PROGRESS" className="text-xs font-bold">In Progress</SelectItem>
                                <SelectItem value="COMPLETED" className="text-xs font-bold">Completed</SelectItem>
                                <SelectItem value="CANCELLED" className="text-xs font-bold">Cancelled</SelectItem>
                              </SelectContent>
                            </Select>

                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteSession(session.id)}
                              data-testid={`delete-session-${session.id}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Create Knowledge Transfer Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="w-[min(96vw,600px)] max-w-[600px] max-h-[92vh] flex flex-col rounded-2xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 p-0 overflow-hidden shadow-2xl gap-0">
          <DialogHeader className="p-6 pb-4 border-b border-slate-100 dark:border-slate-800 shrink-0">
            <DialogTitle className="text-xl font-bold text-[#143A62] dark:text-slate-100 flex items-center gap-2">
              <Brain className="h-5 w-5 text-blue-500" />
              <span>New Knowledge Transfer</span>
            </DialogTitle>
            <DialogDescription className="text-xs font-semibold text-slate-400 mt-1 tracking-wider">
              Establish a mentorship transfer cycle parameters
            </DialogDescription>
          </DialogHeader>
          <div className="p-6 space-y-4 overflow-y-auto flex-1 custom-scrollbar">
            <div className="space-y-1.5">
              <Label htmlFor="title" className="text-xs font-bold text-slate-400 tracking-wider">Transfer Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Python Backend Development Training"
                className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-sm h-11"
                data-testid="kt-title-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="description" className="text-xs font-bold text-slate-400 tracking-wider">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe what will be taught and learned"
                rows={3}
                className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-semibold text-sm"
                data-testid="kt-description-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="mentor" className="text-xs font-bold text-slate-400 tracking-wider">Mentor *</Label>
                <Select value={formData.mentor_id} onValueChange={(value) => setFormData({ ...formData, mentor_id: value })}>
                  <SelectTrigger className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-sm h-11 shadow-sm" data-testid="kt-mentor-select">
                    <SelectValue placeholder="Select mentor" />
                  </SelectTrigger>
                  <SelectContent className="border-slate-100 dark:border-slate-800">
                    {employees
                      .filter((emp: any) => emp.id !== formData.mentee_id)
                      .map((emp: any) => (
                        <SelectItem key={emp.id} value={emp.id} className="text-sm font-bold">{emp.first_name} {emp.last_name || ''}</SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="mentee" className="text-xs font-bold text-slate-400 tracking-wider">Mentee *</Label>
                <Select value={formData.mentee_id} onValueChange={(value) => setFormData({ ...formData, mentee_id: value })}>
                  <SelectTrigger className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-sm h-11 shadow-sm" data-testid="kt-mentee-select">
                    <SelectValue placeholder="Select mentee" />
                  </SelectTrigger>
                  <SelectContent className="border-slate-100 dark:border-slate-800">
                    {employees
                      .filter((emp: any) => emp.id !== formData.mentor_id)
                      .map((emp: any) => (
                        <SelectItem key={emp.id} value={emp.id} className="text-sm font-bold">{emp.first_name} {emp.last_name || ''}</SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="target_date" className="text-xs font-bold text-slate-400 tracking-wider">Target Completion Date *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-medium",
                      !formData.target_date && "text-slate-400"
                    )}
                    data-testid="kt-target-date-btn"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4 text-slate-400" />
                    {formData.target_date ? format(new Date(formData.target_date), "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 border-slate-100 dark:border-slate-800 rounded-xl shadow-xl" align="start">
                  <Calendar
                    mode="single"
                    selected={formData.target_date ? new Date(formData.target_date) : undefined}
                    onSelect={(date) => {
                      if (date) {
                        setFormData({ ...formData, target_date: format(date, 'yyyy-MM-dd') });
                      }
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
          <DialogFooter className="p-6 pt-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/10 shrink-0 flex gap-2">
            <Button variant="outline" onClick={() => setIsCreateOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={createMutation.isPending} data-testid="create-kt-btn">
              {createMutation.isPending ? 'Creating...' : 'Create Transfer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Session Dialog */}
      <Dialog open={isSessionDialogOpen} onOpenChange={(open) => {
        setIsSessionDialogOpen(open);
        if (!open) {
          setEditingSession(null);
          resetSessionForm();
        }
      }}>
        <DialogContent className="w-[min(96vw,500px)] max-w-[500px] max-h-[92vh] flex flex-col rounded-2xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 p-0 overflow-hidden shadow-2xl gap-0">
          <DialogHeader className="p-6 pb-4 border-b border-slate-100 dark:border-slate-800 shrink-0">
            <DialogTitle className="text-xl font-bold text-[#143A62] dark:text-slate-100 flex items-center gap-2">
              <Video className="h-5 w-5 text-blue-500" />
              <span>{editingSession ? 'Edit Mentorship Session' : 'Schedule New Session'}</span>
            </DialogTitle>
            <DialogDescription className="text-xs font-semibold text-slate-400 mt-1 tracking-wider">
              {editingSession ? 'Update mentorship session parameters' : `Add new session details to plan`}
            </DialogDescription>
          </DialogHeader>
          <div className="p-6 space-y-4 overflow-y-auto flex-1 custom-scrollbar">
            <div className="space-y-1.5">
              <Label htmlFor="session-title" className="text-xs font-bold text-slate-400 tracking-wider">Session Title *</Label>
              <Input
                id="session-title"
                value={sessionFormData.title}
                onChange={(e) => setSessionFormData({ ...sessionFormData, title: e.target.value })}
                placeholder="e.g., Introduction to Python Basics"
                className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-sm h-11"
                data-testid="session-title-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="session-description" className="text-xs font-bold text-slate-400 tracking-wider">Description</Label>
              <Textarea
                id="session-description"
                value={sessionFormData.description}
                onChange={(e) => setSessionFormData({ ...sessionFormData, description: e.target.value })}
                placeholder="What will be covered in this session?"
                rows={2}
                className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-semibold text-sm"
                data-testid="session-description-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-bold text-slate-400 tracking-wider">Scheduled Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-medium",
                        sessionLeaveConflicts.length > 0 && "border-rose-300"
                      )}
                      data-testid="session-date-btn"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4 text-slate-400" />
                      {format(sessionFormData.scheduled_date, "PPP")}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 border-slate-100 dark:border-slate-800 rounded-xl shadow-xl" align="start">
                    <Calendar
                      mode="single"
                      selected={sessionFormData.scheduled_date}
                      onSelect={(date) => {
                        if (date) {
                          const newDate = new Date(date);
                          newDate.setHours(sessionFormData.scheduled_date.getHours());
                          newDate.setMinutes(sessionFormData.scheduled_date.getMinutes());
                          setSessionFormData({ ...sessionFormData, scheduled_date: newDate });
                        }
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="session-time" className="text-xs font-bold text-slate-400 tracking-wider">Time</Label>
                <Input
                  id="session-time"
                  type="time"
                  value={format(sessionFormData.scheduled_date, "HH:mm")}
                  onChange={(e) => {
                    const [hours, minutes] = e.target.value.split(':').map(Number);
                    const newDate = new Date(sessionFormData.scheduled_date);
                    newDate.setHours(hours, minutes);
                    setSessionFormData({ ...sessionFormData, scheduled_date: newDate });
                  }}
                  className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-sm h-11"
                  data-testid="session-time-input"
                />
              </div>
            </div>

            {/* Leave Conflict Warning */}
            {sessionLeaveConflicts.length > 0 && (
              <Alert variant="destructive" className="border-rose-100 bg-rose-50/50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-400 p-4 rounded-xl" data-testid="leave-conflict-alert">
                <AlertTriangle className="h-4 w-4 text-rose-500 shrink-0" />
                <AlertDescription className="space-y-1 ml-2">
                  <strong className="text-xs font-bold">Leave Conflict Detected:</strong>
                  <ul className="list-disc list-inside text-[11px] font-semibold space-y-0.5">
                    {sessionLeaveConflicts.map((conflict, index) => (
                      <li key={index}>{conflict}</li>
                    ))}
                  </ul>
                  <p className="text-[10px] font-bold text-rose-400 mt-1.5">Please select another date when everyone is available.</p>
                </AlertDescription>
              </Alert>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="duration" className="text-xs font-bold text-slate-400 tracking-wider">Duration</Label>
              <Select
                value={String(sessionFormData.duration_minutes)}
                onValueChange={(value) => setSessionFormData({ ...sessionFormData, duration_minutes: Number(value) })}
              >
                <SelectTrigger className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-sm h-11 shadow-sm" data-testid="session-duration-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border-slate-100 dark:border-slate-800">
                  <SelectItem value="30" className="text-xs font-bold">30 minutes</SelectItem>
                  <SelectItem value="45" className="text-xs font-bold">45 minutes</SelectItem>
                  <SelectItem value="60" className="text-xs font-bold">1 hour</SelectItem>
                  <SelectItem value="90" className="text-xs font-bold">1.5 hours</SelectItem>
                  <SelectItem value="120" className="text-xs font-bold">2 hours</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="meeting-link" className="text-xs font-bold text-slate-400 tracking-wider">Meeting Link</Label>
              <Input
                id="meeting-link"
                value={sessionFormData.meeting_link}
                onChange={(e) => setSessionFormData({ ...sessionFormData, meeting_link: e.target.value })}
                placeholder="https://zoom.us/j/..."
                className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-sm h-11"
                data-testid="session-meeting-link-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="notes" className="text-xs font-bold text-slate-400 tracking-wider">Notes</Label>
              <Textarea
                id="notes"
                value={sessionFormData.notes}
                onChange={(e) => setSessionFormData({ ...sessionFormData, notes: e.target.value })}
                placeholder="Any additional notes or preparation instructions"
                rows={2}
                className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-semibold text-sm"
                data-testid="session-notes-input"
              />
            </div>
          </div>
          <DialogFooter className="p-6 pt-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/10 shrink-0 flex gap-2">
            <Button variant="outline" onClick={() => {
              setIsSessionDialogOpen(false);
              setEditingSession(null);
              resetSessionForm();
            }}>Cancel</Button>
            <Button
              onClick={handleSaveSession}
              disabled={createSessionMutation.isPending || updateSessionMutation.isPending || sessionLeaveConflicts.length > 0}
              data-testid="save-session-btn"
            >
              {(createSessionMutation.isPending || updateSessionMutation.isPending)
                ? 'Saving...'
                : editingSession ? 'Update Session' : 'Schedule Session'
              }
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default KnowledgeTransfers;
